import { useEffect, useMemo, useRef, useState } from 'react';
import type { LatLngBoundsExpression, LatLngExpression, Marker as LeafletMarkerInstance } from 'leaflet';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { UnitfieldIcon } from '@unitfield/ui/icons';
import { LoadingRegion } from '@unitfield/ui/components/ui/pixel-grid';
import { cn } from '@unitfield/ui/lib/utils';
import { resolveContentState } from '../lib/content-state';
import { usePublicRouteBridge } from '../public-route-bridge';
import { publicListingQueryKey, publicRentalDataPaths, rentalUnitPath } from '../public-route-contracts';
import { BrandHeader, PublicFooter, PublicSpinner, usePublicShell } from '../shell';
import shell from '../shell/shell.module.css';
import { ListingSentence } from './listing-sentence';
import { ListingResults, unitLocationState } from './listing-cards';
import {
  filterUnits,
  formatRent,
  hasListingFilters,
  parseIsoDate,
  parseListingSearch,
  priceBounds as derivePriceBounds,
  sortByFit,
  startOfDay,
  unitFacts,
  unitPhotos,
  unitTitle,
  writeListingSearch,
  type ListingSearch,
  type PublicListingUnit,
  type RentRange,
} from './listing-model';
import styles from './listing.module.css';

export type { PublicListingUnit } from './listing-model';

export interface PublicRentalApplicationsListingCoreProps {
  organizationSlug: string;
  /** Stories and the settings preview pin the day; the page uses today. */
  now?: Date;
}

/** The API clamps pages at 100; one page holds every published home for most businesses. */
const LISTING_PAGE_SIZE = 100;
const BLANK_FILTERS = { beds: '', baths: '', rentMin: '', rentMax: '', availableFrom: '', petFriendly: '', leaseTermMonths: '' };

type ListingView = 'list' | 'map';

interface ListingPayload {
  items: PublicListingUnit[];
  total: number;
  page?: number;
  pageSize?: number;
}

interface MapCoordinate {
  lat: number;
  lng: number;
  source: 'unit' | 'geocode';
}

interface ListingLocationGroup {
  key: string;
  label: string;
  query: string | null;
  coordinate: MapCoordinate | null;
  units: PublicListingUnit[];
}

interface ResolvedListingLocationGroup extends ListingLocationGroup {
  geocodeStatus: 'idle' | 'loading' | 'ready' | 'missing';
}

interface GeocodeState {
  status: 'loading' | 'ready' | 'missing';
  coordinate?: MapCoordinate;
}

interface GeocodeApiResponse {
  success?: boolean;
  data?: {
    verified?: boolean;
    geometry?: {
      location?: {
        lat?: number;
        lng?: number;
      };
    };
  };
}

export function normalizeListingData(data: unknown): ListingPayload {
  const fallback = { items: [], total: 0 };
  if (!data || typeof data !== 'object') return fallback;
  const payload = data as Record<string, unknown>;
  const candidateItems = payload.items || payload.units || payload.data;
  const items = Array.isArray(candidateItems) ? (candidateItems as PublicListingUnit[]) : [];
  const total = typeof payload.total === 'number' ? payload.total : items.length;
  const page = typeof payload.page === 'number' ? payload.page : undefined;
  const pageSize = typeof payload.pageSize === 'number' ? payload.pageSize : undefined;
  return { items, total, page, pageSize };
}

function normalizeSearchText(value: string | number | null | undefined): string {
  if (value === undefined || value === null) return '';

  return String(value)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function compactSearchText(value: string | number | null | undefined): string {
  return normalizeSearchText(value).replace(/\s+/g, '');
}

const US_STATE_SEARCH_PAIRS = [
  ['AL', 'Alabama'],
  ['AK', 'Alaska'],
  ['AZ', 'Arizona'],
  ['AR', 'Arkansas'],
  ['CA', 'California'],
  ['CO', 'Colorado'],
  ['CT', 'Connecticut'],
  ['DE', 'Delaware'],
  ['FL', 'Florida'],
  ['GA', 'Georgia'],
  ['HI', 'Hawaii'],
  ['ID', 'Idaho'],
  ['IL', 'Illinois'],
  ['IN', 'Indiana'],
  ['IA', 'Iowa'],
  ['KS', 'Kansas'],
  ['KY', 'Kentucky'],
  ['LA', 'Louisiana'],
  ['ME', 'Maine'],
  ['MD', 'Maryland'],
  ['MA', 'Massachusetts'],
  ['MI', 'Michigan'],
  ['MN', 'Minnesota'],
  ['MS', 'Mississippi'],
  ['MO', 'Missouri'],
  ['MT', 'Montana'],
  ['NE', 'Nebraska'],
  ['NV', 'Nevada'],
  ['NH', 'New Hampshire'],
  ['NJ', 'New Jersey'],
  ['NM', 'New Mexico'],
  ['NY', 'New York'],
  ['NC', 'North Carolina'],
  ['ND', 'North Dakota'],
  ['OH', 'Ohio'],
  ['OK', 'Oklahoma'],
  ['OR', 'Oregon'],
  ['PA', 'Pennsylvania'],
  ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'],
  ['SD', 'South Dakota'],
  ['TN', 'Tennessee'],
  ['TX', 'Texas'],
  ['UT', 'Utah'],
  ['VT', 'Vermont'],
  ['VA', 'Virginia'],
  ['WA', 'Washington'],
  ['WV', 'West Virginia'],
  ['WI', 'Wisconsin'],
  ['WY', 'Wyoming'],
  ['DC', 'District of Columbia'],
] as const;

const US_STATE_SEARCH_ALIASES = US_STATE_SEARCH_PAIRS.reduce<Record<string, string[]>>((aliases, [abbreviation, name]) => {
  const normalizedAbbreviation = normalizeSearchText(abbreviation);
  const normalizedName = normalizeSearchText(name);
  const compactName = compactSearchText(name);
  const terms = Array.from(new Set([
    normalizedAbbreviation,
    normalizedName,
    compactName,
    ...normalizedName.split(' '),
  ])).filter(Boolean);

  aliases[normalizedAbbreviation] = terms;
  aliases[normalizedName] = terms;
  aliases[compactName] = terms;
  return aliases;
}, {});

function propertyLocation(unit: PublicListingUnit) {
  return [unit.city, unit.state].filter(Boolean).join(', ');
}

function addressLine(unit: PublicListingUnit) {
  return [unit.address1, unit.city, unit.state].filter(Boolean).join(', ') || propertyLocation(unit) || 'Location pending';
}

function searchFieldVariants(value: string | number | null | undefined): string[] {
  const normalized = normalizeSearchText(value);
  if (!normalized) return [];

  const compact = compactSearchText(value);
  const aliasTerms = US_STATE_SEARCH_ALIASES[normalized] ?? US_STATE_SEARCH_ALIASES[compact] ?? [];

  return Array.from(new Set([
    normalized,
    compact !== normalized ? compact : '',
    ...aliasTerms,
  ])).filter(Boolean);
}

function buildListingSearchCorpus(unit: PublicListingUnit): { text: string; terms: Set<string> } {
  const fields = [
    unit.unitName,
    unit.propertyName,
    unit.address1,
    unit.address2,
    unit.city,
    unit.state,
    unit.postalCode,
    unit.country,
    propertyLocation(unit),
    addressLine(unit),
  ];

  const text = normalizeSearchText(fields.flatMap(searchFieldVariants).join(' '));
  return {
    text,
    terms: new Set(text.split(' ').filter(Boolean)),
  };
}

function buildSearchNeedles(search: string): string[] {
  const normalized = normalizeSearchText(search);
  if (!normalized) return [];

  const compact = compactSearchText(search);
  const normalizedTokens = normalized.split(' ').filter((token) => token.length > 1 || /^\d+$/.test(token));

  if (normalizedTokens.length === 0 && compact.length === 2 && US_STATE_SEARCH_ALIASES[compact]) {
    return [compact];
  }

  return Array.from(new Set(normalizedTokens));
}

function unitMatchesSearch(unit: PublicListingUnit, search: string): boolean {
  const needles = buildSearchNeedles(search);
  if (needles.length === 0) return true;

  const corpus = buildListingSearchCorpus(unit);
  return needles.every((needle) => {
    if (needle.length <= 2) return corpus.terms.has(needle);
    return corpus.text.includes(needle);
  });
}

export function filterUnitsBySearch(units: PublicListingUnit[], search: string): PublicListingUnit[] {
  if (!normalizeSearchText(search)) return units;
  return units.filter((unit) => unitMatchesSearch(unit, search));
}

function getDirectCoordinate(unit: PublicListingUnit): MapCoordinate | null {
  if (typeof unit.latitude !== 'number' || typeof unit.longitude !== 'number') return null;
  if (!Number.isFinite(unit.latitude) || !Number.isFinite(unit.longitude)) return null;
  return { lat: unit.latitude, lng: unit.longitude, source: 'unit' };
}

function getLocationKey(unit: PublicListingUnit) {
  const direct = getDirectCoordinate(unit);
  if (direct) return `point:${direct.lat.toFixed(5)},${direct.lng.toFixed(5)}`;

  const city = unit.city?.trim().toLowerCase() || '';
  const state = unit.state?.trim().toLowerCase() || '';
  if (city || state) return `place:${city}:${state}`;

  return `unknown:${unit.propertyId || unit.propertyName || unit.publicUnitKey || 'location'}`;
}

function getLocationLabel(unit: PublicListingUnit) {
  return propertyLocation(unit) || unit.propertyName || 'Location pending';
}

function getLocationQuery(unit: PublicListingUnit) {
  const cityState = propertyLocation(unit);
  if (!cityState) return null;
  return `${cityState}, ${unit.country || 'US'}`;
}

function buildLocationGroups(units: PublicListingUnit[]): ListingLocationGroup[] {
  const map = new Map<string, ListingLocationGroup>();

  for (const unit of units) {
    const key = getLocationKey(unit);
    const current = map.get(key);

    if (current) {
      current.units.push(unit);
      continue;
    }

    map.set(key, {
      key,
      label: getLocationLabel(unit),
      query: getLocationQuery(unit),
      coordinate: getDirectCoordinate(unit),
      units: [unit],
    });
  }

  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
}

async function geocodeLocation(group: ListingLocationGroup, signal: AbortSignal): Promise<GeocodeState> {
  if (!group.query || !publicRentalDataPaths.geocode) return { status: 'missing' };

  const response = await fetch(publicRentalDataPaths.geocode(group.query), { signal });
  if (!response.ok) return { status: 'missing' };

  const json = (await response.json()) as GeocodeApiResponse;
  const location = json.data?.geometry?.location;
  if (!json.success || !json.data?.verified || typeof location?.lat !== 'number' || typeof location?.lng !== 'number') {
    return { status: 'missing' };
  }

  return {
    status: 'ready',
    coordinate: {
      lat: location.lat,
      lng: location.lng,
      source: 'geocode',
    },
  };
}

function useListingLocations(units: PublicListingUnit[]) {
  const groups = useMemo(() => buildLocationGroups(units), [units]);
  const [geocodes, setGeocodes] = useState<Record<string, GeocodeState>>({});
  const requestedGeocodeKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const targets = groups.filter((group) => {
      if (group.coordinate || !group.query) return false;
      if (requestedGeocodeKeysRef.current.has(group.key)) return false;
      return true;
    });
    if (targets.length === 0) return;

    const controller = new AbortController();
    targets.forEach((group) => requestedGeocodeKeysRef.current.add(group.key));

    setGeocodes((prev) => ({
      ...prev,
      ...Object.fromEntries(targets.map((group) => [group.key, { status: 'loading' as const }])),
    }));

    void Promise.all(
      targets.map(async (group) => {
        try {
          const result = await geocodeLocation(group, controller.signal);
          setGeocodes((prev) => ({ ...prev, [group.key]: result }));
        } catch {
          if (!controller.signal.aborted) {
            setGeocodes((prev) => ({ ...prev, [group.key]: { status: 'missing' } }));
          }
        }
      }),
    );

    return () => {
      controller.abort();
      targets.forEach((group) => requestedGeocodeKeysRef.current.delete(group.key));
    };
  }, [groups]);

  const locations = useMemo<ResolvedListingLocationGroup[]>(() => groups.map((group) => {
    if (group.coordinate) {
      return { ...group, geocodeStatus: 'ready' };
    }

    const geocode = geocodes[group.key];
    return {
      ...group,
      coordinate: geocode?.coordinate ?? null,
      geocodeStatus: geocode?.status ?? 'idle',
    };
  }), [geocodes, groups]);

  return {
    locations,
    isGeocoding: locations.some((location) => location.geocodeStatus === 'loading'),
  };
}

function unitSelectionKey(unit: PublicListingUnit, index: number) {
  return unit.publicUnitKey || unit.unitId || `${unit.propertyName || 'property'}-${unit.unitName || 'unit'}-${index}`;
}

function hashString(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = ((hash << 5) - hash + value.charCodeAt(index)) | 0;
  }
  return Math.abs(hash);
}

const LEAFLET_FALLBACK_CENTER: LatLngExpression = [40.7128, -74.006];
const OPEN_STREET_MAP_TILE_URL = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
const OPEN_STREET_MAP_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

type ReactLeafletRuntime = Pick<
  typeof import('react-leaflet'),
  'MapContainer' | 'TileLayer' | 'Marker' | 'Popup' | 'ZoomControl'
>;

type LeafletRuntime = Pick<typeof import('leaflet'), 'divIcon'>;

interface LeafletModules {
  reactLeaflet: ReactLeafletRuntime;
  leaflet: LeafletRuntime;
}

interface UnitLeafletMarker {
  key: string;
  unit: PublicListingUnit;
  index: number;
  position: LatLngExpression;
  locationLabel: string;
}

interface MarkerClickHandlers {
  marker: LeafletMarkerInstance;
  click: () => void;
}

function buildLeafletMarkers(units: PublicListingUnit[], locations: ResolvedListingLocationGroup[]): UnitLeafletMarker[] {
  const locationByKey = new Map(locations.map((location) => [location.key, location]));
  const groupProgress = new Map<string, number>();
  const groupSizes = new Map<string, number>();

  units.forEach((unit) => {
    const locationKey = getLocationKey(unit);
    groupSizes.set(locationKey, (groupSizes.get(locationKey) ?? 0) + 1);
  });

  return units.map((unit, index) => {
    const key = unitSelectionKey(unit, index);
    const locationKey = getLocationKey(unit);
    const location = locationByKey.get(locationKey);
    const coordinate = location?.coordinate;
    const itemIndex = groupProgress.get(locationKey) ?? 0;
    const groupSize = groupSizes.get(locationKey) ?? 1;
    groupProgress.set(locationKey, itemIndex + 1);

    const baseLat = coordinate?.lat ?? (Array.isArray(LEAFLET_FALLBACK_CENTER) ? Number(LEAFLET_FALLBACK_CENTER[0]) : 40.7128);
    const baseLng = coordinate?.lng ?? (Array.isArray(LEAFLET_FALLBACK_CENTER) ? Number(LEAFLET_FALLBACK_CENTER[1]) : -74.006);
    const hash = hashString(key);
    let lat = baseLat;
    let lng = baseLng;

    if (groupSize > 1) {
      const angle = itemIndex * 2.399963229728653;
      const ring = Math.sqrt(itemIndex + 1);
      const spread = 0.0028 + Math.min(0.01, groupSize * 0.00022);
      lat += Math.sin(angle) * ring * spread;
      lng += Math.cos(angle) * ring * spread;
    } else if (!coordinate) {
      lat += ((hash % 9) - 4) * 0.002;
      lng += (((hash >> 4) % 9) - 4) * 0.002;
    }

    return {
      key,
      unit,
      index,
      position: [lat, lng],
      locationLabel: location?.label ?? (propertyLocation(unit) || 'Location pending'),
    };
  });
}

function buildLeafletBounds(markers: UnitLeafletMarker[]): LatLngBoundsExpression | undefined {
  if (markers.length === 0) return undefined;
  return markers.map((marker) => marker.position) as LatLngBoundsExpression;
}

function RentalLeafletMap({
  units,
  onOpenUnit,
}: {
  units: PublicListingUnit[];
  onOpenUnit: (unit: PublicListingUnit) => void;
}) {
  const { locations, isGeocoding } = useListingLocations(units);
  const [leafletModules, setLeafletModules] = useState<LeafletModules | null>(null);
  const selectUnitRef = useRef(onOpenUnit);
  const markerClickHandlersRef = useRef(new Map<string, MarkerClickHandlers>());
  const markers = useMemo(() => buildLeafletMarkers(units, locations), [locations, units]);
  const markerKeySignature = markers.map((marker) => marker.key).sort().join('|');
  const previewResetKey = useMemo(() => ({}), [markerKeySignature]);
  const [previewState, setPreviewState] = useState(() => ({
    key: previewResetKey,
    markerKey: null as string | null,
  }));
  const previewResetKeyRef = useRef(previewResetKey);
  const bounds = useMemo(() => buildLeafletBounds(markers), [markers]);
  const boundsKey = markers.map((marker) => `${marker.key}:${(marker.position as [number, number]).join(',')}`).join('|');
  const openPreviewMarkerKey = previewState.key === previewResetKey ? previewState.markerKey : null;
  const openPreviewMarker = openPreviewMarkerKey
    ? markers.find((marker) => marker.key === openPreviewMarkerKey) ?? null
    : null;
  const selectedMarkerKey = openPreviewMarker?.key ?? null;

  useEffect(() => {
    previewResetKeyRef.current = previewResetKey;
  }, [previewResetKey]);

  useEffect(() => {
    selectUnitRef.current = onOpenUnit;
  }, [onOpenUnit]);

  useEffect(() => {
    const activeMarkerKeys = new Set(markers.map((marker) => marker.key));
    markerClickHandlersRef.current.forEach((entry, key) => {
      if (!activeMarkerKeys.has(key)) {
        entry.marker.off('click', entry.click);
        markerClickHandlersRef.current.delete(key);
      }
    });
  }, [markers]);

  useEffect(() => () => {
    markerClickHandlersRef.current.forEach((entry) => entry.marker.off('click', entry.click));
    markerClickHandlersRef.current.clear();
  }, []);

  useEffect(() => {
    let mounted = true;

    async function loadLeaflet() {
      const [reactLeaflet, leaflet] = await Promise.all([
        import('react-leaflet'),
        import('leaflet'),
      ]);

      if (mounted) {
        setLeafletModules({ reactLeaflet, leaflet });
      }
    }

    void loadLeaflet();

    return () => {
      mounted = false;
    };
  }, []);

  if (!leafletModules) {
    return (
      <LoadingRegion
        busy
        label="Loading rental map"
        aria-label="Loading rental map"
        className={`${styles.map} rental-map-shell is-loading`}
        indicator={
          <div className={styles.mapLoading}>
            <PublicSpinner size={36} label="Loading rental map" />
            <p>Loading map</p>
          </div>
        }
      />
    );
  }

  const { MapContainer, TileLayer, Marker, Popup, ZoomControl } = leafletModules.reactLeaflet;
  const { divIcon } = leafletModules.leaflet;

  function bindMarkerClick(markerKey: string, markerInstance: LeafletMarkerInstance | null) {
    const existing = markerClickHandlersRef.current.get(markerKey);

    if (!markerInstance) {
      if (existing) {
        existing.marker.off('click', existing.click);
        markerClickHandlersRef.current.delete(markerKey);
      }
      return;
    }

    if (existing?.marker === markerInstance) return;
    if (existing) existing.marker.off('click', existing.click);

    const click = () => setPreviewState({ key: previewResetKeyRef.current, markerKey });
    markerInstance.on('click', click);
    markerClickHandlersRef.current.set(markerKey, { marker: markerInstance, click });
  }

  return (
    <div className={`${styles.map} rental-map-shell`} aria-label="Map of homes">
      <MapContainer
        key={boundsKey || 'fallback-map'}
        bounds={bounds}
        boundsOptions={{ padding: [72, 72], maxZoom: 13 }}
        center={LEAFLET_FALLBACK_CENTER}
        zoom={11}
        minZoom={3}
        maxZoom={18}
        zoomControl={false}
        scrollWheelZoom
        dragging
        className="rental-leaflet-map"
      >
        <TileLayer attribution={OPEN_STREET_MAP_ATTRIBUTION} url={OPEN_STREET_MAP_TILE_URL} />
        <ZoomControl position="topright" />
        {markers.map((marker) => {
          const selected = marker.key === selectedMarkerKey;
          const icon = divIcon({
            className: cn('rental-leaflet-pin', selected && 'is-selected'),
            html: `<span>${marker.index + 1}</span>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
            popupAnchor: [0, -18],
          });

          return (
            <Marker
              key={marker.key}
              ref={(markerInstance) => bindMarkerClick(marker.key, markerInstance)}
              position={marker.position}
              icon={icon}
            />
          );
        })}

        {openPreviewMarker ? (
          <Popup
            key={openPreviewMarker.key}
            position={openPreviewMarker.position}
            className="rental-leaflet-popup"
            closeButton={false}
            autoPan
            keepInView
            autoPanPadding={[24, 24]}
            autoClose={false}
            closeOnClick={false}
            minWidth={288}
            maxWidth={288}
          >
            <RentalMapPreviewCard
              unit={openPreviewMarker.unit}
              index={openPreviewMarker.index}
              locationLabel={openPreviewMarker.locationLabel}
              onOpenDetails={() => selectUnitRef.current(openPreviewMarker.unit)}
            />
          </Popup>
        ) : null}
      </MapContainer>

      {isGeocoding ? <span className={styles.mapCaption} aria-live="polite">Finding places</span> : null}
    </div>
  );
}

function RentalMapPreviewCard({
  unit,
  onOpenDetails,
}: {
  unit: PublicListingUnit;
  index: number;
  locationLabel: string;
  onOpenDetails: () => void;
}) {
  const cover = unitPhotos(unit)[0];
  const rent = formatRent(unit.rent);

  return (
    <button
      type="button"
      className={styles.mapCard}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenDetails();
      }}
      aria-label={`See ${unitTitle(unit)}`}
    >
      <span className={styles.mapCardPhoto}>{cover ? <img src={cover} alt="" decoding="async" draggable={false} /> : null}</span>
      <span className={styles.mapCardBody}>
        {rent ? <span className={styles.rent}><span className={styles.num}>{rent}</span><small> / mo</small></span> : null}
        <span className={styles.cardTitle}>{unitTitle(unit)}</span>
        <span className={styles.cardFacts}>{unitFacts(unit)}</span>
      </span>
    </button>
  );
}

async function fetchListingPage(params: URLSearchParams, signal?: AbortSignal): Promise<ListingPayload> {
  const response = await fetch(
    `${publicRentalDataPaths.listings}?${params.toString()}`,
    { signal },
  );
  const result = await response.json().catch(() => null) as { success?: boolean; message?: string; data?: unknown } | null;
  if (!response.ok || !result?.success) {
    throw new Error(result?.message || 'These homes could not be loaded.');
  }
  return normalizeListingData(result.data);
}

function ViewSwitch({ view, onChange }: { view: ListingView; onChange: (view: ListingView) => void }) {
  return (
    <div className={styles.viewSwitch} role="group" aria-label="Show homes as">
      {([['list', 'List', 'grid'], ['map', 'Map', 'map']] as const).map(([value, label, icon]) => (
        <button key={value} type="button" aria-pressed={view === value} onClick={() => onChange(value)}>
          <UnitfieldIcon name={icon} size={16} aria-hidden="true" />
          {label}
        </button>
      ))}
    </div>
  );
}

function homesLabel(count: number, filtered: boolean): string {
  if (!filtered) return count === 1 ? '1 home' : `${count} homes`;
  return count === 1 ? '1 home fits' : `${count} homes fit`;
}

export function PublicRentalApplicationsListingView({ organizationSlug, now }: PublicRentalApplicationsListingCoreProps) {
  const { searchParams, setSearchParams, navigate } = usePublicRouteBridge();
  const { branding } = usePublicShell();
  const filters: ListingSearch = parseListingSearch(searchParams);
  const today = useMemo(() => startOfDay(now ?? new Date()), [now]);
  const [view, setView] = useState<ListingView>('list');
  const [serverPage, setServerPage] = useState(1);

  const updateFilters = (next: Partial<ListingSearch>) => {
    setServerPage(1);
    setSearchParams((previous) => writeListingSearch(previous, next), { replace: true });
  };

  // One unfiltered fetch: filtering, the date fit, the total and the price bounds all come from it.
  const allHomesQuery = useQuery({
    queryKey: publicListingQueryKey(organizationSlug, BLANK_FILTERS),
    enabled: Boolean(organizationSlug),
    queryFn: ({ signal }) => fetchListingPage(
      new URLSearchParams({ page: '1', pageSize: String(LISTING_PAGE_SIZE) }),
      signal,
    ),
  });
  // Past the API's page size, filter on the server instead. The move-in date never filters:
  // homes free later still show, with how much later.
  const serverFiltering = (allHomesQuery.data?.total ?? 0) > LISTING_PAGE_SIZE;
  const serverQuery = useQuery({
    queryKey: [...publicListingQueryKey(organizationSlug, {
      ...BLANK_FILTERS,
      beds: filters.beds === 'studio' ? '0' : filters.beds ?? '',
      rentMin: filters.rentMin ? String(filters.rentMin) : '',
      rentMax: filters.rentMax ? String(filters.rentMax) : '',
    }), serverPage] as const,
    enabled: serverFiltering,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => {
      const params = new URLSearchParams({ page: String(serverPage), pageSize: String(LISTING_PAGE_SIZE) });
      if (filters.beds) params.set('beds', filters.beds === 'studio' ? '0' : filters.beds);
      if (filters.rentMin) params.set('rentMin', String(filters.rentMin));
      if (filters.rentMax) params.set('rentMax', String(filters.rentMax));
      return fetchListingPage(params, signal);
    },
  });
  const activeQuery = serverFiltering ? serverQuery : allHomesQuery;
  const listingContentState = resolveContentState({
    data: activeQuery.data,
    isLoading: allHomesQuery.isLoading || (serverFiltering && serverQuery.isLoading),
    error: activeQuery.error,
  });

  const allHomes = allHomesQuery.data?.items ?? [];
  const rawItems = activeQuery.data?.items ?? [];
  // A stable array matters: the map's geocoding keys on it.
  const items = useMemo(
    () => sortByFit(
      filterUnits(filterUnitsBySearch(rawItems, filters.search), { beds: filters.beds, rentMin: filters.rentMin, rentMax: filters.rentMax }),
      parseIsoDate(filters.moveIn),
      today,
    ),
    [rawItems, filters.search, filters.beds, filters.rentMin, filters.rentMax, filters.moveIn, today],
  );
  const bounds = useMemo(() => derivePriceBounds(allHomes.map((unit) => unit.rent)), [allHomes]);
  // The price card's live count; past the page size the client cannot know it.
  const countMatches = useMemo(() => {
    if (serverFiltering) return null;
    const searched = filterUnitsBySearch(allHomes, filters.search);
    return (range: RentRange) => filterUnits(searched, { beds: filters.beds, ...range }).length;
  }, [serverFiltering, allHomes, filters.search, filters.beds]);
  const filtered = hasListingFilters(filters);
  const fitCount = serverFiltering ? (filters.search ? items.length : serverQuery.data?.total ?? items.length) : items.length;
  const allCount = allHomesQuery.data?.total ?? allHomes.length;
  const pageCount = serverFiltering ? Math.ceil((serverQuery.data?.total ?? 0) / LISTING_PAGE_SIZE) : 1;
  const name = branding.name;

  const openUnit = (unit: PublicListingUnit) => {
    if (unit.publicUnitKey) navigate(rentalUnitPath(organizationSlug, unit.publicUnitKey, filters.moveIn));
  };

  return (
    <div className="rental-public-listing-page">
      <div className={shell.page}>
        <BrandHeader>
          <div className={styles.ask}>
            <ListingSentence search={filters} priceBounds={bounds} countMatches={countMatches} today={today} onChange={updateFilters} />
          </div>
        </BrandHeader>

        {listingContentState.status === 'loading' ? (
          <LoadingRegion
            busy
            label="Loading homes"
            className={styles.loading}
            indicator={<PublicSpinner size={36} label="Loading homes" />}
          />
        ) : listingContentState.status === 'error' ? (
          <div className={styles.empty} role="alert">
            <h2>These homes didn&rsquo;t load.</h2>
            <button type="button" className={cn(shell.btn, shell.btnTonal)} onClick={() => void activeQuery.refetch()}>Try again</button>
          </div>
        ) : (
          <>
            {items.length > 0 ? <div className={styles.resultsBar}>
              <h2 className={styles.count}>
                <span className={styles.num}>{homesLabel(fitCount, filtered)}</span>
                {filtered && allCount !== fitCount ? (
                  <small className={styles.num}>of {allCount}{name ? <span className={styles.fromName}> from {name}</span> : null}</small>
                ) : null}
              </h2>
              <ViewSwitch view={view} onChange={setView} />
            </div> : null}

            {items.length === 0 ? (
              <div className={styles.empty}>
                {filtered || allCount > 0 ? (
                  <>
                    <h2>No homes fit that.</h2>
                    <button
                      type="button"
                      className={cn(shell.btn, shell.btnTonal)}
                      onClick={() => updateFilters({ beds: null, rentMin: null, rentMax: null, moveIn: null, search: '' })}
                    >
                      Show all homes
                    </button>
                  </>
                ) : (
                  <h2>{name ? `${name} has no homes listed right now.` : 'No homes are listed right now.'}</h2>
                )}
              </div>
            ) : view === 'map' ? (
              <RentalLeafletMap units={items} onOpenUnit={openUnit} />
            ) : (
              <ListingResults
                units={items}
                organizationSlug={organizationSlug}
                organizationName={name}
                moveIn={filters.moveIn}
                today={today}
              />
            )}

            {serverFiltering && pageCount > 1 ? (
              <div className={styles.pager}>
                <button type="button" className={cn(shell.pill)} disabled={serverPage <= 1} onClick={() => setServerPage((page) => page - 1)}>Previous</button>
                <span className={styles.num}>Page {serverPage} of {pageCount}</span>
                <button type="button" className={cn(shell.pill)} disabled={serverPage >= pageCount} onClick={() => setServerPage((page) => page + 1)}>Next</button>
              </div>
            ) : null}
          </>
        )}

        <PublicFooter />
      </div>
    </div>
  );
}

/** Kept for the apply route and the Brand settings preview: the snapshot a card hands forward. */
export { unitLocationState };
